import React, { useCallback, useMemo, useState } from "react";
import { Counterparty } from "@common/types/counterparty";
import { CounterpartyCreationResponse } from "@common/contracts/counterparty.contract";
import { useCounterpartyVerifiedToast } from "@hooks/useCounterpartyVerifiedToast";
import type { ValueControlProps } from "@design-system/formControl/formControl.types";
import type { TooltipTriggerButtonProps } from "@design-system/overlay/triggerButtonProps";
import {
    Picker,
    type PickerCellEditor,
} from "@design-system/Picker/Picker.lib";
import { StandardModal } from "@design-system/Modal/Modal";
import { CreateCounterpartyModal } from "@components/counterparty/CreateCounterpartyModal";
import { toPickerLoadMore } from "@helpers/pickerLoadMore";
import { useCreateCounterpartyMutation } from "@api/counterparty.api";
import { ComboboxTriggerButton } from "./ComboboxTriggerButton";
import {
    CounterpartyComboboxPopup,
    type CounterpartyOptionText,
} from "./CounterpartyComboboxPopup";
import { useCounterpartyComboboxSearch } from "./useCounterpartyComboboxSearch";

// Contact/address fields carried alongside id+name so consumers (e.g. the
// invoice "To" field) can surface the counterparty's email and address without
// a second fetch. Extra fields are optional because some callers only know the
// id+name (e.g. spreadsheet cells resolving from a name map).
export type CounterpartyComboboxValue = Pick<Counterparty, "id" | "name"> &
    Partial<
        Pick<
            Counterparty,
            | "companyName"
            | "title"
            | "firstName"
            | "middleName"
            | "lastName"
            | "suffix"
            | "email"
            | "cc"
            | "bcc"
            | "phoneNumber"
            | "street"
            | "street2"
            | "city"
            | "state"
            | "postalCode"
            | "country"
        >
    >;

const toComboboxValue = (
    counterparty: Counterparty,
): CounterpartyComboboxValue => ({
    id: counterparty.id,
    name: counterparty.name,
    companyName: counterparty.companyName,
    title: counterparty.title,
    firstName: counterparty.firstName,
    middleName: counterparty.middleName,
    lastName: counterparty.lastName,
    suffix: counterparty.suffix,
    email: counterparty.email,
    cc: counterparty.cc,
    bcc: counterparty.bcc,
    phoneNumber: counterparty.phoneNumber,
    street: counterparty.street,
    street2: counterparty.street2,
    city: counterparty.city,
    state: counterparty.state,
    postalCode: counterparty.postalCode,
    country: counterparty.country,
});

interface Props extends Pick<
    ValueControlProps<CounterpartyComboboxValue | undefined, HTMLButtonElement>,
    "onBlur" | "invalid"
> {
    selectedCounterparty?: CounterpartyComboboxValue;
    onChange: (counterparty: CounterpartyComboboxValue | undefined) => void;
    disabled?: boolean;
    placeholder?: string;
    searchPlaceholder?: string;
    createButtonText?: string;
    emptyStateText?: string;
    // Label for the "clear selection" item shown at the top of the list when
    // something is already selected. Defaults to the counterparty copy;
    // callers using this for a different entity (e.g. customer) should
    // override it so the clear action reads correctly.
    clearSelectionText?: string;
    // Footer action next to create. Defaults to the counterparty copy;
    // callers using this for a different entity (e.g. customer) should
    // override it so the edit action reads correctly.
    editButtonText?: string;
    createModalTitle?: string;
    readOnly?: boolean;
    hideArrow?: boolean;
    className?: string;
    triggerButtonProps?: TooltipTriggerButtonProps;
    onOpenChange?: (open: boolean) => void;
    onCreationModalOpenChange?: (open: boolean) => void;
    onCreateCounterparty?: () => void;
    hideFooter?: boolean;
    initialOpen?: boolean;
    open?: boolean;
    excludeIds?: string[];
    forTransactionEditing?: boolean;
    renderSelectedCounterparty?: (
        counterparty: CounterpartyComboboxValue | undefined,
    ) => React.ReactNode;
    toCounterpartyOption?: (
        counterparty: CounterpartyComboboxValue,
    ) => CounterpartyOptionText;
    // When provided and a counterparty is selected, renders an "Edit
    // counterparty" action in the dropdown footer next to "Create new
    // counterparty".
    onEditCounterparty?: () => void;
    // Overrides the default trigger button entirely with a Picker trigger
    // part (e.g. `Picker.CellContent` for table cells).
    trigger?: React.ReactNode;
    // Spreadsheet grid editors: the opening key seeds the search and Enter /
    // Tab pick the first result. While a search is typed or loading, only its
    // results are listed, so the clear row or stale rows are never picked.
    cellEditor?: PickerCellEditor;
}

export const CounterpartyCombobox: React.FC<Props> = ({
    selectedCounterparty,
    onChange,
    disabled,
    placeholder = "Select counterparty",
    searchPlaceholder = "Search counterparties...",
    createButtonText = "Create new counterparty",
    emptyStateText = "No matching counterparties",
    clearSelectionText = "No counterparty",
    editButtonText = "Edit counterparty",
    createModalTitle = "Create counterparty",
    readOnly,
    hideArrow,
    className,
    triggerButtonProps,
    onOpenChange,
    onCreationModalOpenChange,
    onCreateCounterparty,
    hideFooter,
    initialOpen = false,
    open,
    excludeIds,
    forTransactionEditing,
    onEditCounterparty,
    renderSelectedCounterparty,
    toCounterpartyOption,
    trigger,
    onBlur,
    invalid,
    cellEditor,
}) => {
    const showCounterpartyVerifiedToast = useCounterpartyVerifiedToast({
        forTransactionEditing,
    });
    const creationMutation = useCreateCounterpartyMutation();
    const [isDropdownOpen, setIsDropdownOpen] = useState(initialOpen);
    const [showCreationModal, setShowCreationModal] = useState(false);
    const isOpen = open ?? isDropdownOpen;
    const {
        typedSearch,
        setTypedSearch,
        counterpartiesQuery,
        isLoading,
        isCellSearching,
        isCellSearchPending,
    } = useCounterpartyComboboxSearch({ isOpen, cellEditor });

    const counterparties = useMemo(() => {
        const excludedIds = new Set(excludeIds);
        return (counterpartiesQuery.data ?? [])
            .filter((counterparty) => !excludedIds.has(counterparty.id))
            .map(toComboboxValue);
    }, [counterpartiesQuery.data, excludeIds]);
    const counterpartiesById = useMemo(
        () =>
            new Map(
                counterparties.map((counterparty) => [
                    counterparty.id,
                    counterparty,
                ]),
            ),
        [counterparties],
    );

    const onCounterpartyCreated = useCallback(
        (creationResponse: CounterpartyCreationResponse) => {
            onChange(creationResponse.counterparty);
            showCounterpartyVerifiedToast(creationResponse);
        },
        [onChange, showCounterpartyVerifiedToast],
    );

    const onCreateNew = useCallback(
        async (name: string) => {
            if (name) {
                setIsDropdownOpen(false);
                try {
                    const res = await creationMutation.mutateAsync({ name });
                    onCounterpartyCreated(res);
                } catch {
                    // mutation error is handled by react-query
                }
                return;
            }
            setIsDropdownOpen(false);
            if (onCreateCounterparty) {
                onCreateCounterparty();
                return;
            }
            setShowCreationModal(true);
            onCreationModalOpenChange?.(true);
        },
        [
            creationMutation,
            onCounterpartyCreated,
            onCreateCounterparty,
            onCreationModalOpenChange,
        ],
    );

    const onCreationModalClosed = useCallback(
        (creationResponse?: CounterpartyCreationResponse) => {
            setShowCreationModal(false);
            if (creationResponse) {
                onCounterpartyCreated(creationResponse);
            }
            onCreationModalOpenChange?.(false);
        },
        [onCounterpartyCreated, onCreationModalOpenChange],
    );

    return (
        <>
            <Picker.Select
                searchable
                cellEditor={cellEditor}
                {...toPickerLoadMore(counterpartiesQuery)}
                value={selectedCounterparty?.id ?? null}
                onValueChange={(counterpartyId) => {
                    if (counterpartyId === null) {
                        onChange(undefined);
                        return;
                    }
                    const counterparty = counterpartiesById.get(counterpartyId);
                    if (counterparty) {
                        onChange(counterparty);
                    }
                }}
                onInputValueChange={(inputValue) => {
                    setTypedSearch(inputValue || undefined);
                }}
                onOpenChange={(nextOpen) => {
                    setIsDropdownOpen(nextOpen);
                    onOpenChange?.(nextOpen);
                }}
                onBlur={onBlur}
                aria-invalid={invalid}
                disabled={disabled || readOnly}
                open={isOpen}
            >
                {trigger ?? (
                    <ComboboxTriggerButton
                        buttonProps={triggerButtonProps}
                        className={className}
                        ariaLabel={
                            renderSelectedCounterparty
                                ? selectedCounterparty?.name
                                : undefined
                        }
                        value={
                            (renderSelectedCounterparty
                                ? renderSelectedCounterparty(
                                      selectedCounterparty,
                                  )
                                : selectedCounterparty?.name) ?? undefined
                        }
                        placeholder={placeholder}
                        hideArrow={hideArrow}
                    />
                )}
                <CounterpartyComboboxPopup
                    counterparties={counterparties}
                    isLoading={isLoading}
                    hasSelection={
                        Boolean(selectedCounterparty) && !isCellSearching
                    }
                    typedSearch={typedSearch}
                    searchPlaceholder={searchPlaceholder}
                    emptyStateText={emptyStateText}
                    clearSelectionText={clearSelectionText}
                    createButtonText={createButtonText}
                    editButtonText={editButtonText}
                    hideFooter={hideFooter || isCellSearchPending}
                    toCounterpartyOption={toCounterpartyOption}
                    onClear={() => onChange(undefined)}
                    onClose={() => setIsDropdownOpen(false)}
                    onCreateNew={(name) => {
                        void onCreateNew(name);
                    }}
                    onEditCounterparty={onEditCounterparty}
                />
            </Picker.Select>
            <StandardModal
                show={showCreationModal}
                onHide={onCreationModalClosed}
                size="lg"
            >
                <CreateCounterpartyModal
                    close={onCreationModalClosed}
                    forTransactionEditing
                    title={createModalTitle}
                />
            </StandardModal>
        </>
    );
};
